import React from "react";
import { useTranslation } from "react-i18next";
import { TransformedOrder } from "../types/orderApiTypes";
import { decodeHtmlEntities } from "../../../../common/helpers/decodeHtmlEntities";

interface OrderItemsSummaryProps {
  order: TransformedOrder;
}

/**
 * Renders a summary of items in a completed order.
 * Uses pre-calculated and formatted gross totals (Price + Tax) from the transformation layer.
 */
const OrderItemsSummary: React.FC<OrderItemsSummaryProps> = ({ order }) => {
  const { t } = useTranslation();
  const items = order.items;

  return (
    <div className="order-page__items-summary">
      <h3 className="order-page__items-summary-title">
        {t("orderReview.itemsTitle", "Order Items")}
        <span className="order-page__items-summary-count">{items.length}</span>
      </h3>

      <div className="order-page__items-summary-list">
        {items.map((item) => {
          const image = item.images[0] || null;
          const decodedName = decodeHtmlEntities(item.name);

          // --- Logic: Check if the item was free ---
          // rawLineGrossTotal is the sum of (Line Total + Line Total Tax)
          // calculated as integers in the transformer.
          const isGift = item.totals.rawLineGrossTotal === 0;

          return (
            <div key={item.id} className="order-page__items-summary-item">
              {/* Image Column */}
              <div className="order-page__items-summary-image">
                {image ? (
                  <img
                    src={image.thumbnail}
                    alt={decodeHtmlEntities(image.alt) || decodedName}
                  />
                ) : (
                  <div className="placeholder-image" />
                )}
                <span className="order-page__items-summary-qty-badge">
                  {item.quantity}
                </span>
              </div>

              {/* Details Column */}
              <div className="order-page__items-summary-details">
                <span className="order-page__items-summary-name">
                  {decodedName}
                </span>
                {item.variation.length > 0 && (
                  <div className="order-page__items-summary-meta">
                    {item.variation.map((v) => (
                      <span key={v.attribute}>
                        {v.attribute}: {decodeHtmlEntities(v.value)}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Price Column */}
              <div className="order-page__items-summary-price">
                {isGift ? (
                  <span className="is-gift">
                    {t("feedback.cart.gift", "Gift!")}
                  </span>
                ) : (
                  /* 
                    We use the pre-formatted gross total. 
                    This ensures consistency and moves formatting logic out of the view.
                  */
                  item.totals.lineGrossTotal
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderItemsSummary;
